import React, { useState, useEffect } from 'react';
import api from '../../utils/axios';
import { Badge } from '../ui/badge';
import { BarChart3, History, Loader2 } from 'lucide-react';

// Gráficos transaccionales del caso (por día, por hora, por tipo).
// Si hasCasosAnteriores=true, expone un toggle para incluir los movimientos
// históricos del cliente (endpoint acepta include_historical=true).
const TransactionCharts = ({ caseId, hasCasosAnteriores }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [includeHistorical, setIncludeHistorical] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/cases/${caseId}/transacciones-chart`, {
          params: includeHistorical ? { include_historical: true } : {},
        });
        setData(res.data);
      } catch { setData(null); }
      finally { setLoading(false); }
    };
    load();
  }, [caseId, includeHistorical]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-slate-400" size={24} /></div>;
  if (!data || data.total_movimientos === 0) return (
    <div className="space-y-4">
      {hasCasosAnteriores && (
        <div className="flex justify-end">
          <label className="flex items-center gap-2 text-xs bg-orange-50 border border-orange-200 rounded-full px-3 py-1 cursor-pointer hover:bg-orange-100 transition-colors" data-testid="toggle-historical-empty">
            <input
              type="checkbox"
              checked={includeHistorical}
              onChange={(e) => setIncludeHistorical(e.target.checked)}
              className="accent-orange-600"
            />
            <History size={12} className="text-orange-600" />
            <span className="font-medium text-orange-800">Incluir histórico del cliente</span>
          </label>
        </div>
      )}
      <div className="text-center py-12 text-slate-400">
        <BarChart3 size={36} className="mx-auto mb-3" />
        <p>Sin datos de transacciones para este caso{includeHistorical ? ' ni sus anteriores' : ''}</p>
        {data?.error && <p className="text-xs text-red-400 mt-1">{data.error}</p>}
      </div>
    </div>
  );

  const porDia = Object.entries(data.por_dia || {});
  const porHora = Object.entries(data.por_hora || {}).map(([h, v]) => [parseInt(h), v]);
  const porTipo = Object.entries(data.por_tipo || {}).sort((a, b) => b[1] - a[1]);
  const maxDia = Math.max(...porDia.map(([, v]) => v), 1);
  const maxHora = Math.max(...porHora.map(([, v]) => v), 1);

  // Stats summary
  const totalTxs = data.total_movimientos;
  const diasActivos = porDia.filter(([, v]) => v > 0).length;
  const horasActivas = porHora.filter(([, v]) => v > 0);
  const horaPico = horasActivas.length ? horasActivas.reduce((a, b) => b[1] > a[1] ? b : a) : null;
  const horasNocturnas = porHora.filter(([h, v]) => v > 0 && (h < 6 || h >= 22));
  const pctNocturno = totalTxs ? Math.round(horasNocturnas.reduce((s, [, v]) => s + v, 0) / totalTxs * 100) : 0;

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2"><BarChart3 size={20} className="text-blue-600" /> Análisis Transaccional</h3>
        <div className="flex items-center gap-2">
          {hasCasosAnteriores && (
            <label className="flex items-center gap-2 text-xs bg-orange-50 border border-orange-200 rounded-full px-3 py-1 cursor-pointer hover:bg-orange-100 transition-colors" data-testid="toggle-historical">
              <input
                type="checkbox"
                checked={includeHistorical}
                onChange={(e) => setIncludeHistorical(e.target.checked)}
                className="accent-orange-600"
              />
              <History size={12} className="text-orange-600" />
              <span className="font-medium text-orange-800">Incluir histórico del cliente</span>
              {includeHistorical && data?.historical_movimientos_count > 0 && (
                <span className="px-1.5 py-0.5 rounded bg-orange-600 text-white text-[10px] font-bold">
                  +{data.historical_movimientos_count}
                </span>
              )}
              {includeHistorical && data?.overlap_movimientos_count > 0 && (
                <span
                  className="px-1.5 py-0.5 rounded bg-amber-500 text-white text-[10px] font-bold"
                  title={`${data.overlap_movimientos_count} movimientos compartidos entre el caso actual y los anteriores fueron deduplicados`}
                >
                  -{data.overlap_movimientos_count} dup
                </span>
              )}
            </label>
          )}
          <Badge className="bg-blue-100 text-blue-700">{totalTxs} movimientos</Badge>
        </div>
      </div>

      {/* KPIs rápidos */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border rounded-lg p-3">
          <p className="text-[10px] text-slate-400 uppercase">Total</p>
          <p className="text-xl font-bold text-slate-800">{totalTxs}</p>
        </div>
        <div className="bg-white border rounded-lg p-3">
          <p className="text-[10px] text-slate-400 uppercase">Días con actividad</p>
          <p className="text-xl font-bold text-blue-600">{diasActivos}</p>
        </div>
        <div className="bg-white border rounded-lg p-3">
          <p className="text-[10px] text-slate-400 uppercase">Hora pico</p>
          <p className="text-xl font-bold text-amber-600">{horaPico ? `${String(horaPico[0]).padStart(2, '0')}:00 (${horaPico[1]})` : '-'}</p>
        </div>
        <div className="bg-white border rounded-lg p-3">
          <p className="text-[10px] text-slate-400 uppercase">% Nocturno (22-06)</p>
          <p className="text-xl font-bold" style={{ color: pctNocturno > 30 ? '#ef4444' : pctNocturno > 10 ? '#f59e0b' : '#10b981' }}>{pctNocturno}%</p>
        </div>
      </div>

      {/* Transacciones por día - tabla horizontal */}
      {porDia.length > 0 && (
        <div>
          <p className="text-sm font-semibold text-slate-700 mb-3">Transacciones por Día</p>
          <div className="space-y-1">
            {porDia.map(([dia, count]) => (
              <div key={dia} className="flex items-center gap-3">
                <span className="w-24 text-xs text-slate-500 font-mono">{dia.slice(5)}</span>
                <div className="flex-1 h-7 bg-slate-100 rounded-md relative overflow-hidden">
                  <div className="h-full rounded-md bg-gradient-to-r from-blue-500 to-blue-400 transition-all flex items-center"
                    style={{ width: `${Math.max((count / maxDia) * 100, 4)}%` }}>
                    <span className="text-[11px] font-bold text-white px-2">{count}</span>
                  </div>
                </div>
                <span className="w-10 text-right text-xs text-slate-400">{totalTxs ? Math.round(count / totalTxs * 100) : 0}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Distribución horaria - barras verticales */}
      <div>
        <p className="text-sm font-semibold text-slate-700 mb-3">Distribución Horaria</p>
        <div className="bg-slate-50 rounded-xl p-4">
          <div className="flex items-end gap-[3px]" style={{ height: '160px' }}>
            {porHora.map(([hora, count]) => {
              const pct = maxHora ? (count / maxHora) * 100 : 0;
              const isNight = hora < 6 || hora >= 22;
              const isPeak = count >= maxHora * 0.8 && count > 0;
              const bg = isPeak ? 'bg-red-500' : isNight ? 'bg-purple-500' : count > 0 ? 'bg-emerald-500' : 'bg-slate-200';
              return (
                <div key={hora} className="flex-1 flex flex-col items-center justify-end h-full">
                  {count > 0 && <span className="text-[10px] font-bold text-slate-700 mb-1">{count}</span>}
                  <div className={`w-full rounded-t-md ${bg} transition-all`}
                    style={{ height: `${count > 0 ? Math.max(pct, 8) : 2}%` }}
                    title={`${String(hora).padStart(2, '0')}:00 → ${count} transacciones`} />
                  <span className={`text-[9px] mt-1 ${isNight ? 'text-purple-600 font-bold' : 'text-slate-400'}`}>{String(hora).padStart(2, '0')}</span>
                </div>
              );
            })}
          </div>
          <div className="flex gap-4 mt-3 text-[10px] text-slate-500 border-t border-slate-200 pt-2">
            <span className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-emerald-500" /> Diurno (06-21)</span>
            <span className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-purple-500" /> Nocturno (22-05)</span>
            <span className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-red-500" /> Pico</span>
          </div>
        </div>
      </div>

      {/* Tipo de operación */}
      {porTipo.length > 0 && (
        <div>
          <p className="text-sm font-semibold text-slate-700 mb-3">Por Tipo de Operación</p>
          <div className="space-y-1.5">
            {porTipo.map(([tipo, count]) => (
              <div key={tipo} className="flex items-center gap-3">
                <span className="w-32 text-xs text-slate-600 text-right truncate" title={tipo}>{tipo || 'Sin tipo'}</span>
                <div className="flex-1 h-7 bg-slate-100 rounded-md relative overflow-hidden">
                  <div className="h-full rounded-md bg-gradient-to-r from-indigo-500 to-indigo-400 flex items-center"
                    style={{ width: `${Math.max((count / totalTxs) * 100, 4)}%` }}>
                    <span className="text-[11px] font-bold text-white px-2">{count}</span>
                  </div>
                </div>
                <span className="w-10 text-right text-xs text-slate-400">{Math.round(count / totalTxs * 100)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionCharts;
